import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import NotyfContext from "../../contexts/NotyfContext";
import { useWhitelabel } from "../../contexts/WhitelabelContext";
import { getWebhooks, toggleActive } from "../../redux/slices/webhook";
import Loader from "../../components/spinners/Loader";
import { Helmet } from "react-helmet-async";
import {
  Card,
  Container,
  Form,
  Table,
  Row,
  Pagination,
  Col,
  Button,
} from "react-bootstrap";
import useDeviceType from "../../hooks/useDeviceType";
import getWebhookData from "./webhookData";
import { useTranslation } from "react-i18next";
import {
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
  defaultColumn,
} from "react-table";
import { FaSortUp, FaSortDown, FaSort, FaPlus } from "react-icons/fa";
import NewWebhook from "./NewWebhook";

const Webhooks = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const webhooks = useSelector((state) => state.webhook.webhooks);
  const { user } = useSelector((state) => state.user);
  const { impersonationUser } = useSelector((state) => state.impersonationUser);
  const notyf = useContext(NotyfContext);
  const [activeUser, setActiveUser] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const { faviconUrl, backgroundColor, fontColor } = useWhitelabel();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";
  const { webhookColumns } = useMemo(() => getWebhookData(), []);
  const [showNewWebhook, setShowNewWebhook] = useState(false);

  useEffect(() => {
    setActiveUser(impersonationUser.id ? impersonationUser : user);
  }, [impersonationUser, user]);

  useEffect(() => {
    const fetchWebhooks = async () => {
      await Promise.all([dispatch(getWebhooks())])
        .catch((error) => {
          notyf.open({
            type: "danger",
            message: error,
            duration: 5000,
            dismissible: false,
            ripple: true,
            position: {
              x: "center",
              y: "top",
            },
          });
        })
        .finally(() => {
          setIsLoading(false);
        });
    };

    if (activeUser.id) {
      fetchWebhooks();
    }
  }, [dispatch, activeUser]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    prepareRow,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns: webhookColumns,
      data: webhooks,
      initialState: { pageIndex: 0 },
      defaultColumn,
    },
    useSortBy,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        ...columns,
        {
          Header: "Active",
          accessor: "isActive",
          Cell: ({ row }) => (
            <Form.Check
              checked={row.original.isActive}
              onChange={async (e) => {
                await dispatch(
                  toggleActive({
                    id: row.original.id,
                    isActive: row.original.isActive,
                  })
                )
                  .then(() => {
                    notyf.open({
                      type: "success",
                      message: "Webhook updated successfully",
                      duration: 5000,
                      ripple: true,
                      dismissible: false,
                      position: {
                        x: "center",
                        y: "top",
                      },
                    });
                  })
                  .catch((error) => {
                    notyf.open({
                      type: "danger",
                      message: "Something went wrong",
                      duration: 5000,
                      ripple: true,
                      dismissible: false,
                      position: {
                        x: "center",
                        y: "top",
                      },
                    });
                  });
              }}
            />
          ),
        },
      ]);
    }
  );

  if (isLoading) {
    return <Loader />;
  }
  return (
    <React.Fragment>
      <Helmet title="Webhooks">
        <link rel="icon" href={faviconUrl} />
      </Helmet>
      <Container fluid className="p-0">
        <Card>
          <Card.Header>
            <Card.Title>Webhooks</Card.Title>
            {isDesktop && (
              <div className="mb-3 d-flex justify-content-end align-items-center">
                <Button
                  variant="primary"
                  style={{
                    border: "none",
                    "--dynamic-bg-color": backgroundColor,
                    "--dynamic-font-color": fontColor,
                  }}
                  onClick={() => setShowNewWebhook(true)}
                >
                  <FaPlus />
                  <span className="ms-2">New Webhook</span>
                </Button>
              </div>
            )}
            {!isDesktop && (
              <div className="d-flex justify-content-center flex-column mb-3">
                <Button
                  variant="primary"
                  style={{
                    border: "none",
                    "--dynamic-bg-color": backgroundColor,
                    "--dynamic-font-color": fontColor,
                    width: "100%",
                    padding: "10px",
                  }}
                  onClick={() => setShowNewWebhook(true)}
                >
                  <FaPlus />
                  <span className="ms-2">New Webhook</span>
                </Button>
              </div>
            )}
          </Card.Header>
          <Card.Body>
            <Table id="invoicesTable" hover responsive {...getTableProps()}>
              <thead>
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th {...column.getHeaderProps()}>
                        <span {...column.getSortByToggleProps()}>
                          {column.render("Header")}
                          <span>
                            {column.sortable ? (
                              column.isSorted ? (
                                column.isSortedDesc ? (
                                  <FaSortUp className="ms-2 " />
                                ) : (
                                  <FaSortDown className="ms-2 " />
                                )
                              ) : (
                                <FaSort className="ms-2" />
                              )
                            ) : null}
                          </span>
                        </span>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {page.map((row, i) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => {
                        return (
                          <td {...cell.getCellProps()}>
                            {cell.render("Cell")}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            {isDesktop && (
              <Row>
                <Col md="8">
                  <span className="mx-2">
                    {t("table.page")}{" "}
                    <strong>
                      {pageIndex + 1} {t("table.of")} {pageOptions.length}
                    </strong>
                  </span>
                  <span className="ms-3 me-2">{t("table.show")}:</span>
                  <Form.Select
                    className="d-inline-block w-auto"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                    }}
                  >
                    {[5, 10, 20, 30, 40, 50].map((pageSize) => (
                      <option key={pageSize} value={pageSize}>
                        {pageSize}
                      </option>
                    ))}
                  </Form.Select>

                  <span className="ms-3 me-2">{t("table.goToPage")}:</span>
                  <Form.Control
                    className="d-inline-block"
                    type="number"
                    defaultValue={pageIndex + 1}
                    onChange={(e) => {
                      const page = e.target.value
                        ? Number(e.target.value) - 1
                        : 0;
                      gotoPage(page);
                    }}
                    style={{ width: "75px" }}
                  />
                </Col>
                <Col md="4">
                  <Pagination className="float-end">
                    <Pagination.First
                      onClick={() => {
                        gotoPage(0);
                      }}
                      disabled={!canPreviousPage}
                    />
                    <Pagination.Prev
                      onClick={() => {
                        previousPage();
                      }}
                      disabled={!canPreviousPage}
                    />
                    <Pagination.Next
                      onClick={() => {
                        nextPage();
                      }}
                      disabled={!canNextPage}
                    />
                    <Pagination.Last
                      onClick={() => {
                        gotoPage(pageCount - 1);
                      }}
                      disabled={!canNextPage}
                    />
                  </Pagination>
                </Col>
              </Row>
            )}
            {!isDesktop && (
              <React.Fragment>
                <div className="invoices-pagination mt-3">
                  <Row className="mb-3">
                    <Col className="d-flex justify-content-center">
                      <Pagination className="float-end">
                        <Pagination.Prev
                          onClick={() => previousPage()}
                          disabled={!canPreviousPage}
                        />
                        <Col className="d-flex align-items-end mx-2 px-3">
                          <span className="mx-2">
                            <strong>
                              {pageIndex + 1} {t("table.of")}{" "}
                              {pageOptions.length}
                            </strong>
                          </span>
                        </Col>
                        <Pagination.Next
                          onClick={() => nextPage()}
                          disabled={!canNextPage}
                        />
                      </Pagination>
                    </Col>
                  </Row>
                  <hr />
                  <Row>
                    <Col xs="auto">
                      <span className="me-2">{t("table.show")}:</span>
                      <Form.Select
                        className="d-inline-block w-auto"
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(Number(e.target.value));
                        }}
                        style={{
                          padding: "0.25rem 2.1rem 0.25rem 0.7rem",
                          border: "none",
                          boxShadow: "none",
                          fontSize: "14px",
                        }}
                      >
                        {[5, 10, 20, 30, 40, 50].map((pageSize) => (
                          <option key={pageSize} value={pageSize}>
                            {pageSize}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col>
                      <div className="float-end">
                        <span className="me-2">{t("table.goToPage")}:</span>
                        <Form.Control
                          className="d-inline-block w-auto"
                          type="number"
                          defaultValue={pageIndex + 1}
                          min={1}
                          max={pageCount}
                          onChange={(e) => {
                            const page = e.target.value
                              ? Number(e.target.value) - 1
                              : 0;
                            gotoPage(page);
                          }}
                          style={{
                            padding: "0.25rem 1.2rem 0.25rem 0.7rem",
                            fontSize: "0.825rem",
                          }}
                        />
                      </div>
                    </Col>
                  </Row>
                </div>
              </React.Fragment>
            )}
          </Card.Body>
        </Card>
        <NewWebhook
          showNewWebhook={showNewWebhook}
          setShowNewWebhook={setShowNewWebhook}
        />
      </Container>
    </React.Fragment>
  );

  return <div>Webhooks</div>;
};

export default Webhooks;
